import {
  Component,
  DoCheck,
  ElementRef,
  forwardRef,
  HostBinding,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NgControl
} from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Subject } from 'rxjs';
import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import Quill from 'quill';
import { SocialUserService } from '../services/social-user.service';
import { UUID } from 'uuid-generator-ts';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'mat-post-input',
  templateUrl: './mat-post-input.component.html',
  styleUrls: ['./mat-post-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MatPostInputComponent),
      multi: true
    },
    {
      provide: MatFormFieldControl,
      useExisting: MatPostInputComponent
    }
  ],
  host: {
    '[id]': 'id',
    '[attr.aria-describedby]': 'describedBy'
  }
})
export class MatPostInputComponent
  implements
    OnInit,
    DoCheck,
    OnDestroy,
    ControlValueAccessor,
    MatFormFieldControl<any> {
  static nextId = 0;
  @HostBinding() id = `quill-material-${MatPostInputComponent.nextId++}`;

  @ViewChild('container', { read: ElementRef, static: true })
  container: ElementRef;

  stateChanges = new Subject<void>();

  quill: any = Quill;
  editor: any;
  controlType = 'mat-post-input';
  errorState = false;
  ngControl: any;
  touched = false;
  focused = false;

  _value: any;

  @Input() outsideContent = new Subject<string>();

  destroyed = new Subject();

  @Input()
  get value(): any {
    return this._value;
  }

  set value(value) {
    this._value = value;
    this.editor.setContents(this._value);
    this.onChange(value);
    this.stateChanges.next();
  }

  @Input()
  get placeholder() {
    return this._placeholder;
  }

  set placeholder(plh) {
    this._placeholder = plh;
    this.stateChanges.next();
  }

  public _placeholder: string;

  @Input()
  get required() {
    return this._required;
  }

  set required(req) {
    this._required = coerceBooleanProperty(req);
    this.stateChanges.next();
  }

  public _required = false;

  @Input()
  get disabled() {
    return this._disabled;
  }

  set disabled(disabled) {
    this._disabled = coerceBooleanProperty(disabled);
    this.stateChanges.next();
  }

  public _disabled = false;

  get empty() {
    const text = this.editor.getText().trim();
    return !text;
  }

  @Input()
  options: any = null;

  @HostBinding('class.floating')
  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  @HostBinding('attr.aria-describedby') describedBy = '';

  setDescribedByIds(ids: string[]) {
    this.describedBy = ids.join(' ');
  }

  constructor(
    public elRef: ElementRef,
    public injector: Injector,
    public fm: FocusMonitor,
    private socialUserService: SocialUserService
  ) {
    fm.monitor(elRef.nativeElement, true).subscribe(origin => {
      this.focused = !!origin;
      this.stateChanges.next();
    });
  }

  ngOnInit(): void {
    // avoid Cyclic Dependency
    this.ngControl = this.injector.get(NgControl);
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }

    const editorRef = this.container.nativeElement.querySelector('.editor');

    const bodyOptions = {
      modules: {
        mention: {
          allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
          mentionDenotationChars: ['@', '#'],
          minChars: 1,
          onSelect: (item, insertItem) => {
            // const editor = this.editor.quillEditor;
            insertItem(item);
            // necessary because quill-mention triggers changes as 'api' instead of 'user'
            // editor.insertText(editor.getLength() - 1, '', 'user');
          },
          source: (searchTerm, renderList, mentionChar) => {
            if (mentionChar === '@') {
              this.socialUserService
                .getSuggestedUsers({ startsWith: searchTerm })
                .subscribe(res => {
                  if (res.data) {
                    const converted = res.data.suggestUsers.map(user => ({
                      id: user.ID,
                      value: user.handle
                    }));
                    renderList(converted, searchTerm);
                  } else {
                    renderList([], searchTerm);
                  }
                });
            }

            if (mentionChar === '#') {
              this.socialUserService
                .getSuggestedHHashtags({
                  startsWith: searchTerm
                })
                .subscribe(res => {
                  if (res.data) {
                    const convertedHash = res.data.suggestHashtags.map(
                      hash => ({
                        id: new UUID().getDashFreeUUID(),
                        value: hash.value.split('#')[1]
                      })
                    );
                    renderList(convertedHash, searchTerm);
                  } else {
                    renderList([], searchTerm);
                  }
                });
            }
          }
        },
        toolbar: false
      },
      heme: 'snow'
    };

    this.editor = new Quill(editorRef, bodyOptions);
    this.editor.on('text-change', () => {
      // this.onChange(this.editor.getContents());
      this.onChange(this.getValue());
    });

    this.outsideContent.pipe(takeUntil(this.destroyed)).subscribe(val => {
      const selection = this.editor.getSelection(true);
      this.editor.insertText(selection, val);
    });
  }

  ngDoCheck(): void {
    if (this.ngControl) {
      this.errorState =
        this.ngControl.invalid && this.ngControl.touched && !this.focused;
      this.stateChanges.next();
    }
  }

  ngOnDestroy() {
    this.stateChanges.complete();
    this.destroyed.next();
    this.destroyed.complete();
    this.fm.stopMonitoring(this.elRef.nativeElement);
  }

  writeValue(delta: any): void {
    this.editor.setContents(delta);
    this._value = delta;
  }

  onChange = (delta: any) => {};

  registerOnChange(fn: (v: any) => void): void {
    this.onChange = fn;
  }

  onTouched = () => {
    this.touched = true;
  };

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  onContainerClick(event: MouseEvent) {
    if (!this.focused) {
      this.editor.focus();
      this.focused = true;
      this.stateChanges.next();
    }
  }

  private getValue(): any | undefined {
    if (!this.editor) {
      return undefined;
    }

    const delta: any = this.editor.getContents();
    if (this.isEmpty(delta)) {
      return undefined;
    }

    return this.quillGetHTML(delta);
  }

  private isEmpty(contents: any): boolean {
    if (contents.ops.length > 1) {
      return false;
    }

    const opsTypes: Array<string> = Object.keys(contents.ops[0]);

    if (opsTypes.length > 1) {
      return false;
    }

    if (opsTypes[0] !== 'insert') {
      return false;
    }

    return contents.ops[0].insert === '\n';
  }

  private quillGetHTML(inputDelta) {
    const tempCont = document.createElement('div');
    new Quill(tempCont).setContents(inputDelta);
    return tempCont.getElementsByClassName('ql-editor')[0].innerHTML;
  }
}
